// GVEP.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './gvep.css';
import './gvep-light.css';
import { ensureGVEPCookie } from './WebserverUser';
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
} from '../shared/xomics-webserver-client';
import CentralApiGuide from '../shared/CentralApiGuide';
import WebserverDemoSection from '../shared/WebserverDemoSection';

import logoDark from '../../GVEP_LOGO.png';
import logoLight from '../../GVEP_LOGO_WHITE.png';
import baseLogo from '../../base_logo.png';
import baseLogoLight from '../../base_logo_light.png';

const THEME_STORAGE_KEY = 'gvep-theme-preference';
const SERVICE_CODE = 'gvep';

// Apply theme immediately on script load to prevent flash
(function applyStoredThemeImmediately() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  let theme = 'dark';
  if (stored === 'light') {
    theme = 'light';
  } else if (stored === 'system') {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    theme = prefersDark ? 'dark' : 'light';
  }
  const root = document.documentElement;
  root.classList.remove('gvep-theme-light', 'gvep-theme-dark');
  root.classList.add(theme === 'light' ? 'gvep-theme-light' : 'gvep-theme-dark');
})();

function GVEP() {
  const [cookieId, setCookieId] = useState('');
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('home');

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pipeline, setPipeline] = useState(null);
  const [uploadUnavailable, setUploadUnavailable] = useState('');
  // The sample types the active pipeline accepts, from the catalogue.
  const [sampleCode, setSampleCode] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    ensureGVEPCookie()
      .then((cookie) => setCookieId(cookie.id))
      .catch((error) => console.error('Failed to register GVEP browser credential:', error));
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) setTheme(stored);
  }, []);

  useEffect(() => {
    if (!cookieId) return;
    loadServiceCatalogue(SERVICE_CODE, cookieId)
      .then((catalogue) => {
        const selected = defaultServicePipeline(catalogue);
        setPipeline(selected);
        setSampleCode(selected.sample_codes?.length === 1 ? selected.sample_codes[0] : '');
        if (!selected.sample_codes?.length) {
          setUploadUnavailable('The active GVEP pipeline has no XOmics sample-type mapping yet. Existing jobs remain available, but new submissions are disabled until that release mapping is added.');
        } else {
          setUploadUnavailable('');
        }
      })
      .catch((error) => setUploadUnavailable(error.message));
  }, [cookieId]);

  // Time formatting helpers
const asDate = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return new Date(value);
  if (/^\d+$/.test(String(value))) return new Date(Number(value));
  const text = String(value);
  return new Date(/[zZ]|[+-]\d\d:\d\d$/.test(text) ? text : `${text}Z`);
};
 const getElapsed = (startedAt) => {
  if (!startedAt) return '';
  const seconds = Math.floor((Date.now() - asDate(startedAt)) / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const getDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return '';
  const seconds = Math.floor((asDate(finishedAt) - asDate(startedAt)) / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

  const getStatusMessage = (job) => {
    switch (job.status) {
      case 'pending_upload': return 'Waiting for upload...';
      case 'queued': return 'In queue';
      case 'processing':
      case 'running': return `Processing... ${getElapsed(job.started_at)}`;
      case 'completed': return `Done (${getDuration(job.started_at, job.finished_at)})`;
      case 'failed': return job.message || 'Failed';
      default: return job.status;
    }
  };

  // Fetch jobs
  const fetchJobs = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoadingJobs(true);
    try {
      if (!cookieId) {
        setJobs([]);
        if (showLoading) setIsLoadingJobs(false);
        return;
      }
      const data = await loadServiceJobs(SERVICE_CODE, cookieId, { limit: 50 });
      setJobs(data.jobs.map((job) => ({
        ...job,
        uuid: job.job_uuid || job.upload_uuid,
        name: job.sample_name,
      })));
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setJobs([]);
    }
    if (showLoading) setIsLoadingJobs(false);
  }, [cookieId]);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll for running/queued jobs every 5 seconds
  useEffect(() => {
    const hasActiveJobs = jobs.some(job => ['queued', 'running', 'processing'].includes(job.status));
    if (!hasActiveJobs) return;

    const interval = setInterval(() => {
      fetchJobs(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  const handleThemeToggle = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);

    const root = document.documentElement;
    root.classList.remove('gvep-theme-light', 'gvep-theme-dark');
    root.classList.add(newTheme === 'light' ? 'gvep-theme-light' : 'gvep-theme-dark');
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase();
      return ext.endsWith('.vcf') || ext.endsWith('.vcf.gz') || ext.endsWith('.gz');
    });

    if (validFiles.length !== files.length) {
      alert('Some files were skipped. Only VCF, VCF.GZ, and GZ files are accepted.');
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (uploadedFiles.length === 0 || !pipeline || uploadUnavailable || !sampleCode) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        // One VCF is one independent GVEP job.
        await createServiceUpload({
          serviceCode: SERVICE_CODE,
          token: cookieId,
          pipeline,
          sampleCode,
          selectedFiles: [{ file, slotPosition: 1, label: 'Input VCF' }],
          displayName: file.name,
          metadata: { original_filename: file.name },
          onProgress: ({ fraction }) => {
            setUploadProgress(((i + fraction) / uploadedFiles.length) * 100);
          },
        });
        setUploadProgress(((i + 1) / uploadedFiles.length) * 100);
      }

      setUploadedFiles([]);
      fetchJobs();
    } catch (err) {
      console.error('Upload error:', err);
      alert(err.message);
    }

    setIsUploading(false);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async (job) => {
    try {
      await openJobDownload(SERVICE_CODE, cookieId, job);
    } catch (err) {
      console.error('Download error:', err);
      alert(err.message);
    }
  };

  const themeClass = theme === 'light' ? 'light-theme' : '';
  const logoSrc = theme === 'light' ? logoLight : logoDark;
  const baseLogoSrc = theme === 'light' ? baseLogoLight : baseLogo;

  return (
    <div className={`GVEP-app-container ${themeClass}`}>
      {/* AppBar Header */}
      <header className="GVEP-header">
        <div className="GVEP-header-left">
          <a href="/" className="GVEP-logo-container">
            <img
              src={baseLogoSrc}
              alt="Home"
              className="GVEP-logo-img"
            />
          </a>
          <nav className="GVEP-nav-tabs">
            <button
              className={`GVEP-nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button
              className={`GVEP-nav-tab ${activeTab === 'api' ? 'active' : ''}`}
              onClick={() => setActiveTab('api')}
            >
              API
            </button>
            <button
              className={`GVEP-nav-tab ${activeTab === 'docs' ? 'active' : ''}`}
              onClick={() => setActiveTab('docs')}
            >
              Docs
            </button>
            <button
              className={`GVEP-nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="GVEP-content-area">
        {activeTab === 'docs' ? (
          <div className="GVEP-docs-container">
            <div className="GVEP-docs-content">
              <div className="GVEP-docs-header">
                <h1>Documentation</h1>
                <p>Learn how to use G-VEP for variant annotation</p>
              </div>

              {/* Quick Start */}
              <div className="GVEP-docs-section">
                <div className="GVEP-docs-section-header">
                  <i className="fas fa-rocket"></i>
                  <h2>Quick Start</h2>
                </div>
                <div className="GVEP-docs-steps">
                  <div className="GVEP-docs-step">
                    <div className="GVEP-docs-step-num">1</div>
                    <div className="GVEP-docs-step-content">
                      <h3>Upload your VCF file</h3>
                      <p>Drag and drop your VCF file onto the upload area, or click to browse. We accept <code>.vcf</code>, <code>.vcf.gz</code>, and <code>.gz</code> files up to 1 GB.</p>
                    </div>
                  </div>
                  <div className="GVEP-docs-step">
                    <div className="GVEP-docs-step-num">2</div>
                    <div className="GVEP-docs-step-content">
                      <h3>Start the analysis</h3>
                      <p>Click "Start Analysis" to submit your file. Your job will be queued and processed automatically using our GPU-accelerated pipeline.</p>
                    </div>
                  </div>
                  <div className="GVEP-docs-step">
                    <div className="GVEP-docs-step-num">3</div>
                    <div className="GVEP-docs-step-content">
                      <h3>Download results</h3>
                      <p>Once complete, click the download button next to your job to get your annotated VCF file with variant effect predictions.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What is G-VEP */}
              <div className="GVEP-docs-section">
                <div className="GVEP-docs-section-header">
                  <i className="fas fa-dna"></i>
                  <h2>What is G-VEP?</h2>
                </div>
                <p className="GVEP-docs-text">
                  G-VEP (GPU-accelerated Variant Effect Predictor) is a high-performance tool for annotating genetic variants. It predicts the functional effects of variants on genes, transcripts, and protein sequences, helping researchers understand the potential impact of genetic changes.
                </p>
                <div className="GVEP-docs-features">
                  <div className="GVEP-docs-feature">
                    <i className="fas fa-bolt"></i>
                    <span>GPU-accelerated processing</span>
                  </div>
                  <div className="GVEP-docs-feature">
                    <i className="fas fa-shield-alt"></i>
                    <span>Secure file handling</span>
                  </div>
                  <div className="GVEP-docs-feature">
                    <i className="fas fa-cloud"></i>
                    <span>No installation required</span>
                  </div>
                </div>
              </div>

              {/* Job Statuses */}
              <div className="GVEP-docs-section">
                <div className="GVEP-docs-section-header">
                  <i className="fas fa-tasks"></i>
                  <h2>Understanding Job Statuses</h2>
                </div>
                <div className="GVEP-docs-status-list">
                  <div className="GVEP-docs-status-item">
                    <span className="GVEP-status-badge queued">queued</span>
                    <span>Your job is waiting in the processing queue</span>
                  </div>
                  <div className="GVEP-docs-status-item">
                    <span className="GVEP-status-badge running">running</span>
                    <span>Your file is being processed</span>
                  </div>
                  <div className="GVEP-docs-status-item">
                    <span className="GVEP-status-badge completed">completed</span>
                    <span>Processing finished - download your results</span>
                  </div>
                  <div className="GVEP-docs-status-item">
                    <span className="GVEP-status-badge failed">failed</span>
                    <span>An error occurred - check the error message</span>
                  </div>
                </div>
              </div>

              {/* Self-Hosting */}
              <div className="GVEP-docs-section">
                <div className="GVEP-docs-section-header">
                  <i className="fas fa-server"></i>
                  <h2>Run Locally</h2>
                </div>
                <p className="GVEP-docs-text">
                  Want to run G-VEP on your own infrastructure? The tool is available as an open-source project.
                </p>
                <a href="https://github.com/Emre040/G-VEP" target="_blank" rel="noopener noreferrer" className="GVEP-docs-repo-link">
                  <i className="fab fa-github"></i>
                  <span>github.com/Emre040/G-VEP</span>
                  <i className="fas fa-external-link-alt"></i>
                </a>
              </div>

              {/* Support */}
              <div className="GVEP-docs-section GVEP-docs-support">
                <div className="GVEP-docs-section-header">
                  <i className="fas fa-life-ring"></i>
                  <h2>Need Help?</h2>
                </div>
                <p className="GVEP-docs-text">
                  For technical support, questions, feature requests, or feedback, reach out to our team:
                </p>
                <a href="mailto:hello@phenomelongevity.com" className="GVEP-docs-email-link">
                  <i className="fas fa-envelope"></i>
                  hello@phenomelongevity.com
                </a>
              </div>

            </div>
          </div>
        ) : activeTab === 'settings' ? (
          <div className="GVEP-settings-container">
            <div className="GVEP-settings-content">
              <div className="GVEP-settings-header">
                <h1>Settings</h1>
                <p>Configure your G-VEP experience</p>
              </div>

              <div className="GVEP-settings-grid">
                <div className="GVEP-settings-section">
                  <div className="GVEP-settings-section-header">
                    <div className="GVEP-settings-section-icon">
                      <i className="fas fa-palette"></i>
                    </div>
                    <div>
                      <h2>Appearance</h2>
                      <p>Customize the look and feel</p>
                    </div>
                  </div>
                  <div className="GVEP-settings-section-content">
                    <div className="GVEP-setting-row">
                      <span className="GVEP-setting-label">Theme</span>
                      <div className="GVEP-theme-toggle">
                        <button
                          className={`GVEP-theme-btn ${theme === 'dark' ? 'active' : ''}`}
                          onClick={() => handleThemeToggle('dark')}
                        >
                          <i className="fas fa-moon"></i>
                        </button>
                        <button
                          className={`GVEP-theme-btn ${theme === 'light' ? 'active' : ''}`}
                          onClick={() => handleThemeToggle('light')}
                        >
                          <i className="fas fa-sun"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="GVEP-settings-section">
                  <div className="GVEP-settings-section-header">
                    <div className="GVEP-settings-section-icon">
                      <i className="fas fa-user-circle"></i>
                    </div>
                    <div>
                      <h2>Account</h2>
                      <p>Your session information</p>
                    </div>
                  </div>
                  <div className="GVEP-settings-section-content">
                    <div className="GVEP-setting-row">
                      <span className="GVEP-setting-label">Account ID</span>
                      <span className="GVEP-setting-value">{cookieId || 'N/A'}</span>
                    </div>
                    <div className="GVEP-setting-row">
                      <span className="GVEP-setting-label">Session</span>
                      <span className="GVEP-setting-value status-active">Active</span>
                    </div>
                  </div>
                </div>

                <div className="GVEP-settings-section">
                  <div className="GVEP-settings-section-header">
                    <div className="GVEP-settings-section-icon">
                      <i className="fas fa-info-circle"></i>
                    </div>
                    <div>
                      <h2>About</h2>
                      <p>Application information</p>
                    </div>
                  </div>
                  <div className="GVEP-settings-section-content">
                    <div className="GVEP-setting-row">
                      <span className="GVEP-setting-label">Application</span>
                      <span className="GVEP-setting-value">G-VEP</span>
                    </div>
                    <div className="GVEP-setting-row">
                      <span className="GVEP-setting-label">Version</span>
                      <span className="GVEP-setting-value">1.0.0</span>
                    </div>
                    <div className="GVEP-setting-row">
                      <span className="GVEP-setting-label">Description</span>
                      <span className="GVEP-setting-value">GPU-Accelerated VEP</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'api' ? (
          <CentralApiGuide
            serviceCode={SERVICE_CODE}
            serviceName="G-VEP"
            token={cookieId}
            unavailableMessage={uploadUnavailable}
          />
        ) : <div className="GVEP-home">
          {/* Banner Header */}
          <header className="GVEP-home-header">
            <div className="GVEP-welcome-container">
              <div className="GVEP-welcome-logo-wrapper">
                <img src={logoSrc} alt="G-VEP Logo" className="GVEP-welcome-logo" />
              </div>
              <div className="GVEP-welcome-text">
                <h1>Welcome to G-VEP</h1>
                <p>GPU-accelerated Variant Effect Predictor for high-throughput annotation</p>
              </div>
            </div>
          </header>

          {/* Content Grid */}
          <div className="GVEP-home-content">
            {/* Left Column - Jobs */}
            <section className="GVEP-left-column">
              <div className="GVEP-section GVEP-jobs-section">
                <h2>Your Jobs</h2>

                <div className="GVEP-jobs-filter">
                  <div className="GVEP-search-input">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending_upload">Pending Upload</option>
                    <option value="queued">Queued</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="GVEP-jobs-list">
                  {isLoadingJobs ? (
                    <div className="GVEP-empty-state">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading...</span>
                    </div>
                  ) : filteredJobs.length === 0 ? (
                    <div className="GVEP-empty-state">
                      <i className="fas fa-folder-open"></i>
                      <span>No jobs found</span>
                    </div>
                  ) : (
                    filteredJobs.map(job => (
                      <div
                        key={job.uuid}
                        className={`GVEP-job-item ${selectedJob?.uuid === job.uuid ? 'selected' : ''}`}
                        onClick={() => setSelectedJob(job)}
                      >
                        <div className="GVEP-job-icon">
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div className="GVEP-job-content">
                          <div className="GVEP-job-title">{job.name}</div>
                          <div className="GVEP-job-meta">{getStatusMessage(job)}</div>
                        </div>
                        {job.status === 'completed' && (
                          <button
                            className="GVEP-job-download"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(job);
                            }}
                            title="Download results"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                        )}
                        <div className={`GVEP-job-status status-${job.status}`}>
                          {job.status === 'completed' && <i className="fas fa-check-circle"></i>}
                          {(job.status === 'running' || job.status === 'processing') && <i className="fas fa-spinner fa-spin"></i>}
                          {job.status === 'queued' && <i className="fas fa-clock"></i>}
                          {job.status === 'pending_upload' && <i className="fas fa-cloud-upload-alt"></i>}
                          {job.status === 'failed' && <i className="fas fa-times-circle"></i>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            {/* Right Column - Upload */}
            <aside className="GVEP-right-column">
              <div className="GVEP-section GVEP-upload-section">
                <h2>Upload Files</h2>

                {uploadUnavailable && (
                  <div className="GVEP-empty-state">
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{uploadUnavailable}</span>
                  </div>
                )}

                {(pipeline?.sample_codes?.length || 0) > 1 && (
                  <div className="GVEP-sample-type">
                    <label htmlFor="gvep-sample-type">Sample type</label>
                    <select
                      id="gvep-sample-type"
                      value={sampleCode}
                      onChange={(event) => setSampleCode(event.target.value)}
                      disabled={isUploading}
                    >
                      <option value="">Select...</option>
                      {pipeline.sample_codes.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Drop Zone */}
                <div
                  className={`GVEP-dropzone ${isDragging ? 'dragging' : ''} ${uploadedFiles.length > 0 ? 'has-files' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".vcf,.vcf.gz,.gz"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div className="GVEP-dropzone-content">
                    <div className="GVEP-dropzone-icon">
                      <i className="fas fa-cloud-upload-alt"></i>
                    </div>
                    <h3>Drop your VCF files here</h3>
                    <p>or click to browse</p>
                    <span className="GVEP-dropzone-hint">VCF, VCF.GZ, GZ</span>
                  </div>
                </div>

                {/* Annotation Chips - only show when no files selected */}
                {uploadedFiles.length === 0 && (
                  <div className="GVEP-annotations">
                    <div className="GVEP-annotations-header">
                      <i className="fas fa-tags"></i>
                      <span>Annotations included:</span>
                    </div>
                    <div className="GVEP-annotation-chips">
                      <span className="GVEP-chip">SpliceAI</span>
                      <span className="GVEP-chip">REVEL</span>
                      <span className="GVEP-chip">CADD</span>
                      <span className="GVEP-chip">BayesDel</span>
                      <span className="GVEP-chip">AlphaMissense</span>
                      <span className="GVEP-chip">dbscSNV</span>
                      <span className="GVEP-chip">ClinVar</span>
                      <span className="GVEP-chip">SIFT</span>
                      <span className="GVEP-chip">PolyPhen-2</span>
                      <span className="GVEP-chip">gnomAD</span>
                      <span className="GVEP-chip">LOFTEE</span>
                      <span className="GVEP-chip">RefSeq</span>
                      <span className="GVEP-chip">HGVS</span>
                    </div>
                  </div>
                )}

                {/* File List */}
                {uploadedFiles.length > 0 && (
                  <div className="GVEP-file-list">
                    <h3><i className="fas fa-list"></i> Files to Upload ({uploadedFiles.length})</h3>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="GVEP-file-item">
                        <div className="GVEP-file-icon">
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div className="GVEP-file-info">
                          <span className="GVEP-file-name">{file.name}</span>
                          <span className="GVEP-file-size">{formatFileSize(file.size)}</span>
                        </div>
                        <button className="GVEP-file-remove" onClick={() => removeFile(index)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Progress */}
                {isUploading && (
                  <div className="GVEP-upload-progress">
                    <div className="GVEP-progress-bar">
                      <div className="GVEP-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                    <span className="GVEP-progress-text">{Math.round(uploadProgress)}% uploaded</span>
                  </div>
                )}

                {/* Submit Button */}
                {uploadedFiles.length > 0 && !isUploading && (
                  <div className="GVEP-submit-section">
                    <button
                      className="GVEP-submit-btn"
                      onClick={handleSubmit}
                      disabled={!pipeline || Boolean(uploadUnavailable) || !sampleCode}
                    >
                      <i className="fas fa-rocket"></i>
                      Start Analysis
                    </button>
                  </div>
                )}
              </div>
              <WebserverDemoSection serviceCode={SERVICE_CODE} />
            </aside>
          </div>
        </div>}
      </main>

      {/* Cookie Badge */}
      <div className="GVEP-cookie-icon">
        <i className="fas fa-cookie-bite"></i>
        <span className="GVEP-tooltip">{`ID: ${cookieId}`}</span>
      </div>
    </div>
  );
}

export default GVEP;
